/**
 * Prefix of the synthetic field name the server gives every hand-placed rectangle.
 *
 * <p>
 * Mirrors {@code SmManualPlacementService#MANUAL_FIELD_NAME_PREFIX}. The server invents the full
 * name ({@code sm.manual.{signerId}.{n}}) and nothing on this side may construct one - the ordinal
 * is counted per signer over the order of the list that was SENT, which only the server knows after
 * it has grouped by role. What the prefix is for is READING: it is how a field returned by the
 * layout endpoint is recognised as hand-placed rather than as an AcroForm field, so a placement
 * session can be resumed with the rectangles that already exist.
 * </p>
 */
export const SM_MANUAL_FIELD_NAME_PREFIX: string = 'sm.manual.';

/**
 * ONE hand-placed signature rectangle, on its way to the server.
 *
 * <h2>The coordinates are PDF points and they are FRACTIONAL</h2>
 * <p>
 * They are sent exactly as the conversion produced them, never rounded. The backend columns are
 * {@code DOUBLE} for that reason: PDF points are fractional, and rounding moves a signature
 * VISIBLY on a rotated page or one whose crop box does not start at the origin - which is precisely
 * where {@code toPdfRect} delegates the arithmetic to the viewport instead of computing it. Rounding
 * at this boundary would undo that care one layer up, and it would do it silently: the rectangle
 * still looks right in the list, and only the stamped document shows the drift.
 * </p>
 *
 * <h2>Origin bottom-left, crop box offset INCLUDED</h2>
 * <p>
 * The same PDF user space {@code SmAdHocRectDto} documents and the backend stamps in. Nothing adds
 * the crop box origin again on the way - it is already in these numbers, and adding it a second time
 * is one of the two ways a rectangle ends up in the cropped-away margin, where a signature is
 * invisible and nobody is told.
 * </p>
 */
export interface SmManualPlacementDto {

    /**
     * Role token of the signer this rectangle belongs to, verbatim.
     *
     * The KEY, not a caption - the backend looks up the process's signer by exactly this string and
     * refuses the whole request when no signer of the process carries it. The tool therefore offers
     * only role keys the process actually has a signer for.
     */
    roleKey: string;

    /** One-based page the rectangle sits on. */
    page: number;

    lowerLeftX: number;
    lowerLeftY: number;
    upperRightX: number;
    upperRightY: number;
}
